/**
 * Keep a focused text field visible above the on-screen keyboard.
 *
 * Telegram's WebView (and mobile browsers) don't reliably scroll a focused input into
 * view inside a custom scroll container, so the user has to hand-scroll to see what
 * they're typing (reported for the profile nickname + other fields). This installs ONE
 * global listener: when a text input / textarea / contenteditable is focused — and on
 * every `visualViewport` resize while one is focused (i.e. the keyboard opening or
 * resizing) — it scrolls that field to the centre of the visible area.
 *
 * Idempotent + inert until `setupInputAutoScroll()` runs (from main.tsx); returns a
 * teardown for tests.
 */

/** How long the viewport must stop moving before we measure anything (the keyboard's
 *  own animation runs ~250–350ms and reports continuously while it does). */
const SETTLE_MS = 140;

const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "range",
  "button",
  "submit",
  "reset",
  "file",
  "color",
  "image",
]);

/** The currently-focused element IFF it is a text-entry field, else null. */
function focusedTextField(): HTMLElement | null {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return null;
  if (el instanceof HTMLTextAreaElement) return el;
  if (el instanceof HTMLInputElement) return NON_TEXT_INPUT_TYPES.has(el.type) ? null : el;
  return el.isContentEditable ? el : null;
}

/**
 * Is the field ALREADY fully inside the visible (keyboard-adjusted) area?
 *
 * `getBoundingClientRect` is in layout-viewport coordinates, and `visualViewport`
 * reports the visible band within that: `[offsetTop, offsetTop + height]`. A margin
 * keeps a field that is merely flush with the edge from counting as "visible".
 */
function alreadyVisible(el: HTMLElement): boolean {
  const vv = window.visualViewport;
  if (!vv) return false;
  const rect = el.getBoundingClientRect();
  if (rect.height === 0) return false; // not laid out (hidden) — nothing to scroll to
  const top = vv.offsetTop + 8;
  const bottom = vv.offsetTop + vv.height - 8;
  return rect.top >= top && rect.bottom <= bottom;
}

export function setupInputAutoScroll(): () => void {
  let raf = 0;

  const bringFocusedIntoView = (): void => {
    const el = focusedTextField();
    if (!el) return;
    // ALREADY VISIBLE ⇒ DO NOTHING. The app shell shrinks to the keyboard-adjusted
    // height (see telegram/viewport.ts applyKeyboardInset), so a chat composer is lifted
    // clear of the keyboard on its own — and `visualViewport` fires resize/scroll many
    // times per second while the keyboard animates. Scrolling on every one of those
    // events forced a layout and yanked the message list mid-animation: the reported
    // "laggy" keyboard transition. The check is one rect read; the scroll only happens
    // when the field genuinely is behind the keyboard (e.g. a mid-form profile field).
    if (alreadyVisible(el)) return;
    cancelAnimationFrame(raf);
    // rAF so we scroll AFTER the browser has applied the keyboard's viewport change.
    raf = requestAnimationFrame(() => el.scrollIntoView({ block: "center" }));
  };

  const onFocusIn = (): void => {
    if (!focusedTextField()) return;
    // The keyboard animates open (~250–350ms) and shrinks the viewport; scroll once it
    // has settled so we centre against the REDUCED area (belt-and-suspenders for
    // platforms whose visualViewport doesn't emit a resize).
    window.setTimeout(bringFocusedIntoView, 300);
  };

  // DO NOTHING WHILE THE KEYBOARD IS MOVING. `visualViewport` fires resize in a burst
  // across the whole animation, and `bringFocusedIntoView` reads a bounding rect — a
  // FORCED SYNCHRONOUS LAYOUT — on each one, right as the shell is being re-laid out.
  // Wait for the burst to stop, then look exactly once.
  let settle = 0;
  const onViewportResize = (): void => {
    window.clearTimeout(settle);
    settle = window.setTimeout(bringFocusedIntoView, SETTLE_MS);
  };

  const vv = window.visualViewport;
  document.addEventListener("focusin", onFocusIn);
  vv?.addEventListener("resize", onViewportResize);

  return () => {
    document.removeEventListener("focusin", onFocusIn);
    vv?.removeEventListener("resize", onViewportResize);
    window.clearTimeout(settle);
    cancelAnimationFrame(raf);
  };
}
